import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Ban, Download } from "lucide-react";
import { toast } from "sonner";
import SignupsView from "@/pages/admin/ReferralSignupsView";

const STATUSES = ["", "pending", "qualified", "rejected", "invalidated", "recorded"];

export default function ReferralsTab() {
  const [view, setView] = useState("signups");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async (query = q, st = status) => {
    try {
      const { data } = await api.get("/admin/referrals", { params: { q: query || undefined, status: st || undefined } });
      setRows(data.referrals);
      setTotal(data.total);
    } catch {
      toast.error("Couldn't load referrals.");
    }
  }, [q, status]);

  useEffect(() => { load("", ""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = async (id) => {
    if (!window.confirm("Invalidate this referral? The referrer's badge will be recomputed.")) return;
    try {
      await api.post(`/admin/referrals/${id}/invalidate`);
      toast.success("Referral invalidated.");
      load();
    } catch {
      toast.error("Invalidate failed.");
    }
  };

  return (
    <div data-testid="admin-referrals-tab" className="space-y-4">
      <div className="flex gap-2">
        {[["signups", "Signups & attribution"], ["rows", "Program rows"]].map(([v, label]) => (
          <button key={v} data-testid={`referrals-view-${v}`} onClick={() => setView(v)}
            className={`text-xs uppercase tracking-widest px-3 py-2 border ${
              view === v ? "border-rose-600 text-rose-400" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}>
            {label}
          </button>
        ))}
      </div>
      {view === "signups" ? <SignupsView /> : (
      <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input data-testid="admin-referrals-search" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search handle or email…" className="max-w-xs bg-zinc-950 border-white/10 rounded-none" />
        <select data-testid="admin-referrals-status" value={status}
          onChange={(e) => { setStatus(e.target.value); load(q, e.target.value); }}
          className="bg-zinc-950 border border-white/10 text-sm px-3 py-2 text-zinc-300">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <span className="text-xs text-zinc-500">{total} total</span>
        <a data-testid="admin-referrals-export" href={`${api.defaults.baseURL}/admin/referrals/export`}
          className="ml-auto inline-flex items-center gap-1.5 text-xs border border-white/15 px-3 py-2 text-zinc-300 hover:bg-white/5">
          <Download size={13} /> Export CSV
        </a>
      </div>
      <div className="overflow-x-auto card-surface">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
              <th className="p-3">Referrer</th><th className="p-3">Referred</th><th className="p-3">Status</th>
              <th className="p-3">Created</th><th className="p-3">Qualified</th><th className="p-3">Flags</th><th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-white/5" data-testid={`admin-referral-row-${r.id}`}>
                <td className="p-3">@{r.referrer_handle || "?"}</td>
                <td className="p-3">@{r.referred_handle || "?"}</td>
                <td className="p-3">
                  <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 ${
                    r.status === "qualified" ? "bg-emerald-500/15 text-emerald-300"
                    : r.status === "pending" ? "bg-zinc-800 text-zinc-400"
                    : "bg-rose-950/60 text-rose-300"}`}>{r.status}</span>
                </td>
                <td className="p-3 text-xs text-zinc-500">{r.created_at?.slice(0, 10)}</td>
                <td className="p-3 text-xs text-zinc-500">{r.qualified_at?.slice(0, 10) || "—"}</td>
                <td className="p-3 text-xs text-rose-400">{(r.rejected_reasons || []).join(", ")}</td>
                <td className="p-3">
                  {(r.status === "pending" || r.status === "qualified") && (
                    <Button data-testid={`admin-referral-invalidate-${r.id}`} onClick={() => invalidate(r.id)}
                      variant="ghost" className="h-7 rounded-none border border-rose-900/50 text-rose-300 text-xs px-2 hover:bg-rose-950/40">
                      <Ban size={12} className="mr-1" /> Invalidate
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="p-6 text-center text-zinc-600 text-xs" colSpan={7}>No referrals yet.</td></tr>}
          </tbody>
        </table>
      </div>
      </div>
      )}
    </div>
  );
}
